import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Shimmer from "../components/Shimmer.jsx";
import { StatCard, STAT_ICONS } from "../components/StatRow.jsx";
import ZoneIndicator from "../components/ZoneIndicator.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useChainTime } from "../hooks/useChainTime.js";
import { useDeposits } from "../hooks/useDeposits.js";
import { useTreasury } from "../hooks/useTreasury.js";
import { useTvt } from "../hooks/useTvt.js";
import { useLendingHealth, useLoansByOwner } from "../hooks/useLoans.js";
import { usePegStatus } from "../hooks/usePegStatus.js";
import { usePortfolioHistory } from "../hooks/usePortfolioHistory.js";
import { useTvtSeries } from "../hooks/useTvtSeries.js";
import { useRecentTransactions } from "../hooks/useRecentTransactions.js";
import { ethers } from "ethers";
import { getReadProvider } from "../services/web3.js";
import { fetchInterestCoverage } from "../services/api.js";
import { chainNowMs, chainNowSec } from "../utils/chainClock.js";
import {
    formatUsdcCompact,
    formatGilCompact,
    formatUsdc,
    parseUsdc,
    liveAccruedInterest,
    liveAccruedInterestSmooth
} from "../utils/format.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import ResultModal from "../components/ResultModal.jsx";

// Matches the contract's MIN_INTEREST_WITHDRAWAL ($10 USDC, 6dp).
const MIN_INTEREST_WITHDRAWAL = 10_000_000n;

/**
 * Dashboard — client-requested layout (May 2026 review):
 *
 *   1. Three headline stat cards (Total Deployed Capital, Total Accrued
 *      Yield, Available for Withdrawal).
 *   2. "Build Your Optimized Portfolio" hero with two side-by-side CTAs:
 *      Deploy Capital (open a new Term Deposit) and Release Liquidity
 *      (borrow against existing Safe Vault balance).
 *   3. Active Positions table summarising every open position the
 *      wallet owns, including any outstanding Capital Advancement
 *      (loan against Safe Vault).
 *   4. Portfolio Actions & Intelligence side panel with status notes.
 *
 * All numbers come from the live deposits feed via `useDeposits`. The
 * mint-green widget grid from the earlier iteration has been removed
 * per client direction.
 */
export default function Dashboard() {
    const { address, signer } = useWallet();
    const { deposits, isLoading, error, refresh } = useDeposits(address);
    const [errorDismissed, setErrorDismissed] = useState(false);
    // §4.2 interest-withdrawal flow (client Jun 2026): user withdraws a
    // chosen amount of accrued interest to their wallet, min $10.
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [withdrawBusy, setWithdrawBusy] = useState(false);
    const [withdrawResult, setWithdrawResult] = useState(null);

    // 1-second ticker so the live yield figures advance visibly — the
    // useMemos below would otherwise recompute only when `deposits`
    // changes and the numbers would sit frozen between fetches.
    // (Hook order: ticker first so the chain-time call below has it.)
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);
    // Keys off chain time (latest block timestamp), not wall clock —
    // matters when testing on a local Hardhat node fast-forwarded via
    // `evm_increaseTime`, where chain runs ahead of wall clock and a
    // wall-clock "now" would compute negative elapsed days against the
    // deposit's chain startTime. Production: chain ≈ wall, no-op.
    const { chainNowSec } = useChainTime();
    // eslint-disable-next-line no-unused-vars
    const _tick = tick; // dependency so per-second re-render triggers
    const nowSec = useMemo(() => BigInt(chainNowSec), [chainNowSec, tick]);

    // Active = state 1 AND chain hasn't crossed maturity yet. Matured-
    // redeemable = states 2..4 OR state 1 past maturityTime (the
    // contract auto-transitions on next settle/rollover, but the UI
    // should treat those rows as matured immediately so the headline
    // numbers + Action Required prompts stay in sync with reality).
    // Closed = states 5/6 (already withdrawn).
    const active = useMemo(
        () =>
            deposits.filter((d) => {
                const s = Number(d.state);
                return s === 1 && chainNowSec < Number(d.maturityTime || 0);
            }),
        [deposits, chainNowSec]
    );
    const matured = useMemo(
        () =>
            deposits.filter((d) => {
                const s = Number(d.state);
                if (s === 2 || s === 3 || s === 4) return true;
                return s === 1 && chainNowSec >= Number(d.maturityTime || 0);
            }),
        [deposits, chainNowSec]
    );

    const totals = useMemo(() => {
        return active.reduce(
            (acc, d) => ({
                principal: acc.principal + BigInt(d.principal),
                interest: acc.interest + BigInt(d.accruedInterest),
                loan: acc.loan + BigInt(d.loanBalance)
            }),
            { principal: 0n, interest: 0n, loan: 0n }
        );
    }, [active]);

    // Projected TOTAL RETURNS (client, Jun 2026): the combined MAXIMUM the
    // wallet will ultimately receive = Σ each position's full-term maturity
    // payout (principal + 3-year interest at its stored rate) MINUS any
    // leverage/loans that must be repaid. Positions deployed a year apart
    // can't all be claimed at once, so this is the combined ceiling, not a
    // claim-now figure. Uses the stored 20%/rung rate (not the 27.5% Turbo
    // headline): summing every rung's payout and netting the loans already
    // yields the ~27.5% effective return on the user's equity.
    const totalReturnsProjected = useMemo(() => {
        const days = BigInt(config.protocol.fixedTermDays);
        let payout = 0n;
        let loans = 0n;
        for (const d of [...active, ...matured]) {
            const principal = BigInt(d.principal || 0);
            const rate = BigInt(Number(d.annualRateBps) || config.protocol.annualRateBps);
            const fullTermInterest = (principal * rate * days) / (10000n * 365n);
            payout += principal + fullTermInterest;
            loans += BigInt(d.loanBalance || 0);
        }
        return payout > loans ? payout - loans : 0n;
    }, [active, matured]);

    // `tick`, `chainNowSec`, `nowSec` already declared above (they
    // power the active/matured partition). They're reused below for
    // the live-yield computations.

    // Total accrued yield for HEADLINE DISPLAY (Dashboard cards).
    // Uses the per-second smoother so the cards visibly tick every
    // second — matching the loan-page behaviour the client asked for.
    // The smoother still respects the contract's 24h floor (returns 0
    // for deposits younger than a day), so the headline never claims
    // interest the contract wouldn't credit. Settlement / exit
    // previews continue to use the day-stepped `liveAccruedInterest`
    // path (in the backend preview controllers) so payout figures
    // remain exactly what the chain will pay.
    const totalAccruedYield = useMemo(() => {
        let total = 0n;
        for (const d of active) {
            // §V6.2 — real accrual rate = 2000 (20%) for both products
            // (Turbo's 27.5% is a display-only label); use the stored rate.
            total += liveAccruedInterestSmooth(
                d.principal,
                d.startTime,
                d.maturityTime,
                nowSec,
                Number(d.annualRateBps) || config.protocol.annualRateBps,
                d.realizedInterest
            );
        }
        return total;
    }, [active, nowSec]);

    // ── §4.2 interest withdrawal ──────────────────────────────────────
    // Day-stepped withdrawable interest (what the contract will actually
    // pay, net of realised) across every Active deposit + the id list for
    // the batch call. Cards show the per-second smoother; the withdraw uses
    // the day-stepped figure so the on-chain amount can never exceed it.
    const withdrawable = useMemo(() => {
        let total = 0n;
        const ids = [];
        for (const d of active) {
            const pend = liveAccruedInterest(
                d.principal, d.startTime, d.maturityTime, nowSec,
                Number(d.annualRateBps) || config.protocol.annualRateBps, d.realizedInterest
            );
            if (pend > 0n) { total += pend; ids.push(Number(d.id)); }
        }
        return { total, ids };
    }, [active, nowSec]);

    const canWithdraw = Boolean(signer) && withdrawable.total >= MIN_INTEREST_WITHDRAWAL;

    function openWithdraw() {
        setWithdrawAmount(formatUsdc(withdrawable.total)); // prefill with the max available
        setWithdrawResult(null);
        setWithdrawOpen(true);
    }

    async function handleWithdrawInterest() {
        let amountWei;
        try { amountWei = parseUsdc(withdrawAmount); } catch { amountWei = 0n; }
        if (amountWei < MIN_INTEREST_WITHDRAWAL) {
            setWithdrawResult({ type: "error", title: "Amount too low", message: "Minimum withdrawal is $10 USDC." });
            return;
        }
        if (amountWei > withdrawable.total) {
            setWithdrawResult({ type: "error", title: "Amount too high", message: `You have $${formatUsdc(withdrawable.total)} of accrued interest available.` });
            return;
        }
        setWithdrawBusy(true);
        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.withdrawInterestAmount(withdrawable.ids, amountWei, overrides);
            await tx.wait();
            await refresh();
            setWithdrawOpen(false);
            setWithdrawResult({ type: "success", title: "Interest Withdrawn", message: `$${formatUsdc(amountWei)} USDC has been sent to your wallet. Your deposits keep earning.` });
        } catch (err) {
            setWithdrawResult({ type: "error", title: "Withdrawal Failed", message: decodeContractError(err) });
        } finally {
            setWithdrawBusy(false);
        }
    }

    // When there isn't yet $10 of accrued interest the on-chain minimum blocks
    // the withdrawal, so the button is disabled BY DESIGN — say so plainly (a
    // greyed "Withdraw" alone reads like a broken feature). `belowMin` is only
    // true once the wallet is connected but the accrued figure is short.
    const belowMin = Boolean(signer) && withdrawable.total < MIN_INTEREST_WITHDRAWAL;
    // Visible hint shown INSIDE the withdrawal cards (not just the button
    // tooltip) so it's obvious how much is claimable and why the button is
    // locked: "You have $2.98 — $10 needed to withdraw".
    const withdrawHint = belowMin
        ? `You have $${formatUsdc(withdrawable.total)} — $10 needed to withdraw`
        : null;
    // Hover tooltip on the value: the estimated date/time the shown accrued
    // balance unlocks for withdrawal. The per-card text (referencing that
    // card's value) is assembled in ThreeStatRow.
    const withdrawUnlockDate = belowMin ? estimateUnlockDate(active, withdrawable.total) : null;
    const withdrawAction = (
        <button
            type="button"
            disabled={!canWithdraw}
            onClick={openWithdraw}
            title={
                canWithdraw
                    ? "Withdraw accrued interest to your wallet"
                    : belowMin
                    ? `Accrued interest must reach $10 to withdraw (you have $${formatUsdc(withdrawable.total)})`
                    : "Connect your wallet to withdraw"
            }
            className="inline-flex items-center gap-1 rounded-full bg-[#FBC206] px-2.5 py-1 font-manrope text-[10.5px] font-bold text-[#10281b] transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/40 disabled:translate-y-0 disabled:shadow-none disabled:hover:translate-y-0"
        >
            {belowMin ? "Min $10 to withdraw" : "Withdraw"}
            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="7 4 13 10 7 16" />
            </svg>
        </button>
    );

    // "Available for Withdrawal" = LIVE accrued interest on Active
    // positions (claimable now) + full principal + interest on Matured
    // positions (term ended, waiting for redemption). The active part
    // uses `totalAccruedYield`, not the indexed `totals.interest` — the
    // latter reads 0 for a fresh deposit until a chain event bumps it,
    // which is why this card was showing nothing.
    const availableForWithdrawal = useMemo(() => {
        const fromMatured = matured.reduce(
            (acc, d) => acc + BigInt(d.principal) + BigInt(d.accruedInterest),
            0n
        );
        return totalAccruedYield + fromMatured;
    }, [totalAccruedYield, matured]);

    // §V6.2 — Borrowing capacity = 75% of Standard Safe Vaults less
    // anything already drawn. Standard SafeVault is 80% of principal.
    // TURBO deposits are EXCLUDED — the contract rejects user-driven
    // loans against them (`NoBorrowingAgainstTurbo`) because the Turbo
    // product already deploys its Safe Vault slice as leverage at
    // creation (routing is 80/10/9/1, same as Standard). Filter on the IMMUTABLE
    // `productMode === 1`, not the mutable compoundMode (which the
    // user can flip On/Off without changing the product itself).
    const availableToBorrow = useMemo(() => {
        let standardPrincipal = 0n;
        let standardLoan = 0n;
        for (const d of active) {
            if (Number(d.productMode) === 1) continue; // Turbo product — no user borrow
            standardPrincipal += BigInt(d.principal);
            standardLoan += BigInt(d.loanBalance);
        }
        const safeVault = (standardPrincipal * BigInt(config.protocol.safeVaultBps)) / 10000n;
        const max = (safeVault * BigInt(config.protocol.maxLtvBps)) / 10000n;
        return max > standardLoan ? max - standardLoan : 0n;
    }, [active]);

    // Aggregate user LTV across all active positions. Used by the Avg LTV
    // gauge so the gauge reflects the wallet's own exposure rather than a
    // protocol average.
    const userLtvBps = useMemo(() => {
        if (totals.principal === 0n) return 0;
        const safeVault = (totals.principal * BigInt(config.protocol.safeVaultBps)) / 10000n;
        if (safeVault === 0n) return 0;
        const ratio = (totals.loan * 10000n) / safeVault;
        return Number(ratio);
    }, [totals.principal, totals.loan]);

    if (!address) {
        return (
            <div className="rounded-2xl border border-[#bfb67b] bg-white p-8 text-center">
                <h2 className="text-xl font-bold text-[#10281b]">Dashboard</h2>
                <p className="mt-2 text-sm text-[#10281b]/70">
                    Connect your wallet to view your portfolio.
                </p>
            </div>
        );
    }

    return (
        <div className="-mt-6 space-y-6">
            {error && !errorDismissed ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[#e0a14c] bg-[#fdf3e3] px-5 py-3.5">
                    <div className="flex items-start gap-2.5">
                        <span
                            className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#e0a14c] text-[11px] font-bold text-white"
                            aria-hidden="true"
                        >
                            !
                        </span>
                        <div>
                            <p className="font-manrope text-[13px] font-semibold text-[#7a4a12]">
                                Couldn't load your portfolio data.
                            </p>
                            <p className="font-manrope text-[12px] text-[#7a4a12]/80">
                                The numbers below may be incomplete. Check your connection and try again.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => refresh()}
                            className="inline-flex items-center rounded-[6px] border border-[#e0a14c] bg-white px-3.5 py-1.5 font-manrope text-[12px] font-bold uppercase tracking-[0.08em] text-[#7a4a12] hover:bg-[#fdf3e3]"
                        >
                            Retry
                        </button>
                        <button
                            type="button"
                            onClick={() => setErrorDismissed(true)}
                            aria-label="Dismiss"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] text-[16px] font-bold text-[#7a4a12]/70 hover:bg-[#fdf3e3]"
                        >
                            ×
                        </button>
                    </div>
                </div>
            ) : null}
            <ThreeStatRow
                principal={totals.principal}
                accruedYield={totalAccruedYield}
                available={availableForWithdrawal}
                isLoading={isLoading}
                withdrawAction={withdrawAction}
                withdrawHint={withdrawHint}
                withdrawUnlockDate={withdrawUnlockDate}
            />

            <OptimizeHero availableToBorrow={availableToBorrow} loading={isLoading} />

            <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
                <ActivePositionsTable
                    deposits={active}
                    loan={totals.loan}
                    isLoading={isLoading}
                />
                <PortfolioActionsCard
                    matured={matured.length}
                    accruedYield={totalAccruedYield}
                    activePrincipal={totals.principal}
                />
            </div>

            <InsightsSection
                ltv={userLtvBps / 100}
                address={address}
                deposits={deposits}
                active={active}
                matured={matured}
                totals={totals}
                accruedYield={totalAccruedYield}
                totalReturns={totalReturnsProjected}
                isLoading={isLoading}
            />

            <WithdrawInterestModal
                open={withdrawOpen}
                available={withdrawable.total}
                amount={withdrawAmount}
                setAmount={setWithdrawAmount}
                busy={withdrawBusy}
                onConfirm={handleWithdrawInterest}
                onClose={() => !withdrawBusy && setWithdrawOpen(false)}
            />
            <ResultModal
                open={withdrawResult !== null && !withdrawOpen}
                type={withdrawResult?.type}
                title={withdrawResult?.title || ""}
                message={withdrawResult?.message || ""}
                onClose={() => setWithdrawResult(null)}
            />
        </div>
    );
}

/**
 * §4.2 — "Withdraw Interest" modal. Lets the owner type how much accrued
 * interest to pull to their wallet (min $10, max = available), with a Max
 * shortcut. Confirms via the aggregate withdrawInterestAmount call.
 */
function WithdrawInterestModal({ open, available, amount, setAmount, busy, onConfirm, onClose }) {
    if (!open) return null;
    let amountWei;
    try { amountWei = parseUsdc(amount || "0"); } catch { amountWei = 0n; }
    const tooLow = amountWei < MIN_INTEREST_WITHDRAWAL;
    const tooHigh = amountWei > available;
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] p-5 text-white">
                <h3 className="font-manrope text-[15px] font-bold text-[#e6c985]">Withdraw Interest</h3>
                <p className="mt-1 text-[11px] text-[#e6c985]/70">
                    Available: <span className="font-semibold text-white">${formatUsdc(available)} USDC</span>. Your deposits keep earning.
                </p>
                <div className="mt-3 flex items-center gap-2 rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-2">
                    <span className="font-manrope text-[18px] font-bold text-white/45">$</span>
                    <input
                        type="number"
                        min="10"
                        step="1"
                        autoFocus
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="flex-1 min-w-0 bg-transparent font-manrope text-[20px] font-bold text-white placeholder:text-white/30 focus:outline-none [color-scheme:dark]"
                        placeholder="0"
                    />
                    <button
                        type="button"
                        onClick={() => setAmount(formatUsdc(available))}
                        className="rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-[#e6c985] hover:bg-white/20"
                    >
                        Max
                    </button>
                </div>
                <p className="mt-1.5 text-[10px] text-[#e6c985]/70">Minimum withdrawal is $10 USDC.</p>
                {tooHigh ? (
                    <p className="mt-1 text-[10px] font-medium text-[#C93030]">You only have ${formatUsdc(available)} available.</p>
                ) : null}
                <div className="mt-4 flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="flex-1 rounded-md bg-white/10 px-4 py-2 text-[12px] font-semibold text-[#e6c985] hover:bg-white/20 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy || tooLow || tooHigh}
                        className="flex-1 rounded-md bg-[#FBC206] px-4 py-2 text-[12px] font-bold text-[#10281b] transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 disabled:translate-y-0 disabled:hover:shadow-none"
                    >
                        {busy ? "Withdrawing…" : "Withdraw"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Estimates the DATE/TIME the accrued interest becomes available to withdraw
 * (i.e. crosses the on-chain withdrawal minimum), from the current live
 * per-second accrual across active deposits. Returns a short formatted string
 * (or null). Used to build the hover tooltip on the accrued-yield value so the
 * user sees exactly when their shown balance unlocks. Continuous approximation
 * of the day-stepped figure — accurate to well under a day.
 */
function estimateUnlockDate(active, currentWei) {
    let perSec = 0; // USDC base units accrued per second across all deposits
    for (const d of active) {
        const p = Number(d.principal || 0);
        const rate = Number(d.annualRateBps) || config.protocol.annualRateBps;
        perSec += (p * rate) / (10000 * 365 * 86400);
    }
    if (perSec <= 0) return null;
    const remaining = Number(MIN_INTEREST_WITHDRAWAL - currentWei);
    if (remaining <= 0) return null;
    const when = new Date(chainNowMs() + (remaining / perSec) * 1000);
    return when.toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
}

/* -------------------------------------------------------------------------- */
/*  Three headline stat cards                                                 */
/* -------------------------------------------------------------------------- */

function ThreeStatRow({ principal, accruedYield, available, isLoading, withdrawAction, withdrawHint, withdrawUnlockDate }) {
    // Tooltip references the card's own displayed value + the unlock date.
    const accruedValue = `$${formatUsdcCompact(accruedYield)}`;
    const availableValue = `$${formatUsdcCompact(available)}`;
    const items = [
        {
            label: "Total Deployed Capital",
            value: `$${formatUsdcCompact(principal)}`,
            unit: "USDC",
            delta: "Across all active positions",
            iconSrc: STAT_ICONS.holdings
        },
        {
            label: "Total Accrued Yield",
            value: `$${formatUsdcCompact(accruedYield)}`,
            unit: "USDC",
            // Show the claimable-vs-minimum hint in place of the default
            // subtitle when the balance is below the $10 withdrawal floor.
            delta: withdrawHint || "Accrued on active positions",
            iconSrc: STAT_ICONS.interest,
            action: withdrawAction,
            valueTitle: withdrawUnlockDate ? `${accruedValue} — withdrawable from ${withdrawUnlockDate}` : null
        },
        {
            label: "Available for Withdrawal",
            value: availableValue,
            unit: "USDC",
            delta: withdrawHint || "Interest + matured principal",
            iconSrc: STAT_ICONS.safe,
            action: withdrawAction,
            valueTitle: withdrawUnlockDate ? `${availableValue} — withdrawable from ${withdrawUnlockDate}` : null
        }
    ];
    return (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
                <StatCard key={item.label} {...item} loading={isLoading} />
            ))}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  "Build Your Optimized Portfolio" hero                                     */
/* -------------------------------------------------------------------------- */

function OptimizeHero({ availableToBorrow, loading = false }) {
    const borrowLabel = `$${formatUsdcCompact(availableToBorrow)} USDC`;
    return (
        <section className="rounded-[14px] border border-[#bfb67b]/40 bg-[#10281b] px-6 py-6 sm:px-8 sm:py-7 ring-1 ring-[#FBC206]/10">
            <div className="grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-stretch">
                {/* Left — Deploy Capital pitch + CTA */}
                <div className="flex flex-col">
                    <h2 className="font-serif text-[26px] font-medium leading-[1.15] text-[#b98c23] sm:text-[30px] md:text-[34px]">
                        Build Your Optimised Portfolio.
                    </h2>
                    <p className="mt-3 max-w-[520px] font-manrope text-[14px] leading-[22px] text-white/85">
                        Deploy capital into structured Term Deposits.
                    </p>
                    <Link
                        to="/deposit"
                        className="mt-6 inline-flex items-center justify-center gap-2 rounded-[8px] border-2 border-[#bfb67b] bg-[#2d3d22] px-8 py-3 font-manrope text-[14px] font-bold uppercase tracking-[0.16em] text-[#e6c985] hover:-translate-y-0.5 hover:bg-[#3a4d2c] hover:shadow-lg transition-all sm:py-3.5"
                    >
                        Deploy Capital
                    </Link>
                </div>

                {/* Right — Release Liquidity card */}
                <div className="flex flex-col rounded-[12px] border-2 border-[#e6c985] bg-[#162e1f] px-5 py-5 sm:px-6 shadow-[0px_1px_22px_14px_rgba(230,201,133,0.29)]">
                    <h3 className="font-serif text-[18px] font-medium leading-[1.2] text-[#b98c23] sm:text-[20px]">
                        Release Liquidity:
                    </h3>
                    <div className="mt-1 font-serif text-[22px] font-semibold leading-[1.15] text-[#e6c985] sm:text-[26px]">
                        {loading ? (
                            <Shimmer width="10rem" height="1.6rem" tone="light" />
                        ) : (
                            `${borrowLabel} available`
                        )}
                    </div>
                    <p className="mt-3 font-manrope text-[12.5px] leading-[18px] text-white/75">
                        Borrow against your principal reserves today. Zero
                        liquidation risk until full term maturity.
                    </p>
                    <Link
                        to="/loans/lending"
                        className="mt-auto inline-flex items-center justify-center gap-2 rounded-[6px] border border-[#bfb67b] bg-transparent px-4 py-2 font-manrope text-[12px] font-bold uppercase tracking-[0.16em] text-[#e6c985] hover:bg-[#2d3d22] transition-colors"
                    >
                        Access Capital
                    </Link>
                </div>
            </div>
        </section>
    );
}

/* -------------------------------------------------------------------------- */
/*  Active Positions table                                                    */
/* -------------------------------------------------------------------------- */

function ActivePositionsTable({ deposits, loan, isLoading }) {
    return (
        // `min-w-0` is critical: without it, a wide table inside this grid
        // item would force the grid track to expand and push the sibling
        // Portfolio Actions card narrower / off-screen. The 2fr/1fr ratio
        // only holds when both items consent to be clipped at the track's
        // intrinsic width — that's what `min-w-0` enables.
        //
        // `self-start` keeps the section from auto-stretching to match
        // the sibling card's height (a grid track default). Without it
        // the section grows tall, the inner max-h container is followed
        // by empty white space and the user perceives "no scroll".
        <section className="min-w-0 self-start overflow-hidden rounded-[12px] border border-[#bfb67b] bg-white">
            <header className="border-b border-[#bfb67b]/40 px-5 py-3.5">
                <h3 className="font-manrope text-[15px] font-semibold text-[#10281b]">
                    Active Positions
                </h3>
            </header>
            {/* Inline `maxHeight` so the cap is enforced even if a
                Tailwind JIT cache hasn't picked up the arbitrary class.
                `overflow: auto` gives BOTH X and Y scroll inside the
                table — the surrounding grid stays a stable width. The
                sticky header keeps column labels visible while
                vertical-scrolling. */}
            <div className="overflow-auto" style={{ maxHeight: 180 }}>
                <table className="min-w-full">
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-[#10281b] text-[#e6c985]">
                            <Th>Asset Type</Th>
                            <Th>Principal Deployed</Th>
                            <Th>Current Yield (uncompounded)</Th>
                            <Th>Advancement Utilisation</Th>
                            <Th>Reinvestment Status</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <EmptyRow text="Loading positions…" />
                        ) : deposits.length === 0 ? (
                            <EmptyRow text="No active positions yet. Deploy capital to open your first Term Deposit." />
                        ) : (
                            <>
                                {deposits.map((d) => (
                                    <PositionRow key={d.id} deposit={d} />
                                ))}
                                {loan > 0n ? (
                                    <LoanSummaryRow loan={loan} />
                                ) : null}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function Th({ children }) {
    return (
        <th className="px-4 py-3 text-left font-manrope text-[11px] font-semibold uppercase tracking-[0.08em] whitespace-nowrap">
            {children}
        </th>
    );
}

function PositionRow({ deposit }) {
    const compound = Number(deposit.compoundMode); // 0 Off, 1 Standard, 2 Turbo
    const isTurbo = compound === 2;
    const assetType = isTurbo
        ? "3 Year Term Deposit (Turbo Automated Reinvestment)"
        : compound === 1
            ? "3 Year Term Deposit (Standard Automated Reinvestment)"
            : "3 Year Term Deposit";
    // Every deposit accrues the SAME contract APR — the compound mode is
    // a reinvestment preference, not a different rate. The old "27.5%"
    // label was a hardcoded marketing figure that misrepresented Turbo
    // auto-compound deposits as having a higher live yield, which the
    // contract never actually credits.
    const yieldLabel = `${config.protocol.annualRateBps / 100}%`;
    const loan = BigInt(deposit.loanBalance || 0);
    const advancement = loan > 0n ? `$${formatUsdcCompact(loan)} drawn` : "N/A";
    const reinvest = compound > 0 ? "Automated (ON)" : "Manual";
    return (
        <tr className="border-b border-black/10 last:border-0">
            <Td>{assetType}</Td>
            <Td>${formatUsdcCompact(deposit.principal)}</Td>
            <Td>{yieldLabel}</Td>
            <Td>{advancement}</Td>
            <Td>{reinvest}</Td>
        </tr>
    );
}

function LoanSummaryRow({ loan }) {
    return (
        <tr className="border-b border-black/10 last:border-0 bg-[#f8f4e6]">
            <Td>Capital Advancement (Leverage Account)</Td>
            <Td>${formatUsdcCompact(loan)}</Td>
            <Td>{config.protocol.loanAprBps / 100}%</Td>
            <Td>${formatUsdcCompact(loan)} drawn</Td>
            <Td>N/A</Td>
        </tr>
    );
}

function Td({ children }) {
    return (
        <td className="px-4 py-3 whitespace-nowrap font-manrope text-[13px] text-black">
            {children}
        </td>
    );
}

function EmptyRow({ text }) {
    return (
        <tr>
            <td
                colSpan="5"
                className="px-4 py-6 text-center font-manrope text-[12px] text-black/60"
            >
                {text}
            </td>
        </tr>
    );
}

/* -------------------------------------------------------------------------- */
/*  Portfolio Actions & Intelligence side panel                                */
/* -------------------------------------------------------------------------- */

function PortfolioActionsCard({ matured, accruedYield, activePrincipal }) {
    const blendedYield = activePrincipal > 0n
        ? ((Number(accruedYield) / Number(activePrincipal)) * 100).toFixed(1)
        : "0.0";
    return (
        <section className="flex flex-col rounded-[12px] border border-[#bfb67b] bg-white px-5 py-5">
            <h3 className="font-manrope text-[15px] font-semibold text-[#10281b]">
                Portfolio Actions & Intelligence
            </h3>
            <ul className="mt-4 space-y-4 text-[13px] leading-[19px] text-black">
                {matured > 0 ? (
                    <li>
                        <span className="font-bold text-[#b98c23]">
                            Term Deposit Cohort matured.
                        </span>{" "}
                        Action Required: redeem or reinvest the principal
                        on your {matured} matured position{matured === 1 ? "" : "s"}.
                    </li>
                ) : (
                    <li>
                        <span className="font-bold text-[#b98c23]">No matured cohorts.</span>{" "}
                        Every active position is still earning yield.
                    </li>
                )}
                <li>
                    <span className="font-bold text-[#b98c23]">
                        Automated Yield Reinvestment ready.
                    </span>{" "}
                    Toggle Automated Reinvestment on any Term Deposit to deploy
                    accrued interest into a fresh cohort.
                </li>
                <li>
                    <span className="font-bold text-[#b98c23]">
                        Current Blended Yield across portfolio:
                    </span>{" "}
                    <span className="font-mono font-semibold">
                        {blendedYield}%
                    </span>
                </li>
            </ul>
        </section>
    );
}

/* -------------------------------------------------------------------------- */
/*  Insights — restored widget grid                                           */
/* -------------------------------------------------------------------------- */

/**
 * Restored from the previous Dashboard iteration. Three staggered
 * columns matching the original Figma. Surfaces use the new
 * cream/dark-green palette (mint green removed); chart geometry and
 * placeholder data are unchanged.
 */
function InsightsSection({ ltv, address, deposits, active, matured, totals, accruedYield, totalReturns, isLoading = false }) {
    return (
        <section className="space-y-3">
            <div className="grid gap-6 lg:grid-cols-[repeat(24,minmax(0,1fr))]">
                <div className="lg:col-span-5 flex flex-col gap-6">
                    <AvgLtvCard ltv={ltv} loading={isLoading} />
                    <AllocationCard deposits={deposits} />
                    <TokensCard />
                </div>

                <div className="lg:col-span-9 flex flex-col gap-6">
                    <TotalReturnsCard address={address} totalReturns={totalReturns} />
                    <RecentTransactionsCard address={address} />
                </div>

                <div className="lg:col-span-10 flex flex-col gap-6">
                    <ActionRequiredCard address={address} deposits={deposits} matured={matured} />
                    <ProtocolHealthCard />
                    <TvgBackingCard />
                </div>
            </div>
        </section>
    );
}

/* -------------------------------------------------------------------------- */
/*  Avg LTV gauge                                                             */
/* -------------------------------------------------------------------------- */

function AvgLtvCard({ ltv, loading = false }) {
    const cx = 80;
    const cy = 95;
    const radius = 65;
    const startDeg = 135;
    const totalDeg = 270;
    const arcLen = ((totalDeg * Math.PI) / 180) * radius;
    const filledLen = arcLen * (Math.max(0, Math.min(100, ltv)) / 100);

    const polar = (deg) => ({
        x: cx + radius * Math.cos((deg * Math.PI) / 180),
        y: cy + radius * Math.sin((deg * Math.PI) / 180)
    });
    const start = polar(startDeg);
    const end = polar(startDeg + totalDeg);
    const dot = polar(startDeg + totalDeg * (ltv / 100));
    const arcPath = `M ${start.x} ${start.y} A ${radius} ${radius} 0 1 1 ${end.x} ${end.y}`;

    // Derive the risk pill from the live LTV instead of hardcoding it, so
    // the badge stays consistent with ActionRequiredCard's liquidation logic.
    const risk = ltv >= config.protocol.liquidationThresholdBps / 100
        ? { label: "Liquidation Risk", cls: "bg-[#3d2222] text-[#f0a0a0] ring-[#f0a0a0]/30", dot: "bg-[#e05555]" }
        : ltv >= config.protocol.maxLtvBps / 100
        ? { label: "High Risk", cls: "bg-[#3d3322] text-[#e6c985] ring-[#e6c985]/30", dot: "bg-[#FBC206]" }
        : ltv >= 50
        ? { label: "Medium Risk", cls: "bg-[#2d3d22] text-[#e6c985] ring-[#e6c985]/30", dot: "bg-[#FBC206]" }
        : { label: "Low Risk", cls: "bg-[#2d3d22] text-[#e6c985] ring-[#e6c985]/30", dot: "bg-[#FBC206]" };

    return (
        <div className="flex flex-col rounded-[10px] border border-[#bfb67b] bg-[#10281b] p-3">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Avg LTV</h3>
            </div>

            <div className="relative mt-2 flex justify-center">
                <svg viewBox="0 0 160 180" overflow="visible" className="h-40 w-40">
                    <path d={arcPath} fill="none" stroke="#ffffff" strokeOpacity="0.1" strokeWidth="7" strokeLinecap="round" />
                    <path
                        d={arcPath}
                        fill="none"
                        stroke="#FBC206"
                        strokeWidth="7"
                        strokeLinecap="round"
                        strokeDasharray={`${filledLen} 99999`}
                    />
                    <circle cx={dot.x} cy={dot.y} r="8" fill="#10281b" stroke="#FBC206" strokeWidth="5" />
                </svg>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-4">
                    <div className="font-manrope text-[22px] font-bold leading-none text-white">
                        {loading ? <Shimmer width="3rem" height="1.4rem" tone="light" /> : `${ltv}%`}
                    </div>
                    <div className="mt-1 text-[10px] font-semibold text-[#e6c985]/80">Loan to Value</div>
                </div>
            </div>

            <div className="mt-3 flex justify-center">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1 text-[11px] font-semibold ring-1 ${risk.cls}`}>
                    {risk.label}
                    <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[9px] font-bold text-[#10281b] ${risk.dot}`}>
                        !
                    </span>
                </span>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Total Returns chart                                                       */
/* -------------------------------------------------------------------------- */

function TotalReturnsCard({ address, totalReturns = 0n }) {
    // Backend-authoritative series. See backend transaction.controller.js
    // → portfolioHistory for the replay logic. The chart is purely a
    // renderer; no client-side computation of the series.
    const [windowId, setWindowId] = useState("7d");
    const { data, isLoading } = usePortfolioHistory(address, { windowId, samples: 61 });
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);
    useEffect(() => {
        if (!pickerOpen) return undefined;
        const close = (event) => {
            if (!pickerRef.current?.contains(event.target)) setPickerOpen(false);
        };
        const esc = (event) => {
            if (event.key === "Escape") setPickerOpen(false);
        };
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", esc);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", esc);
        };
    }, [pickerOpen]);

    const chart = useMemo(() => projectChartFromBackend(data), [data]);
    const currentWindow = WINDOW_OPTIONS.find((w) => w.id === windowId) || WINDOW_OPTIONS[2];

    // Hover state. `hoverIdx` is the chart-sample index nearest the
    // cursor. Defaults to the peak so the tooltip is meaningful before
    // any mouse interaction. Setting back to null restores the peak
    // marker — see svgRef + onPointerMove / onPointerLeave handlers.
    const [hoverIdx, setHoverIdx] = useState(null);
    const svgRef = useRef(null);

    const activeIdx = hoverIdx ?? chart?.peakIdx ?? (chart ? chart.pts.length - 1 : 0);
    const activePt = chart?.pts?.[activeIdx];
    const tooltipText = activePt ? formatTooltip(activePt.v, hoverIdx == null) : "";
    const tooltipWidth = Math.max(110, tooltipText.length * 7.5 + 16);
    const anchor = activePt && activePt.x > 540
        ? "end"
        : activePt && activePt.x < 100
        ? "start"
        : "middle";
    const rectX = anchor === "end" ? -tooltipWidth + 12 : anchor === "start" ? -12 : -tooltipWidth / 2;
    const textX = anchor === "end" ? -6 : anchor === "start" ? 6 : 0;

    function handlePointerMove(event) {
        if (!chart || !svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        // Map cursor → viewBox X. The SVG uses preserveAspectRatio so we
        // scale by the actual aspect-corrected dimensions.
        const viewBoxW = 640;
        const viewBoxH = 260;
        const scale = Math.min(rect.width / viewBoxW, rect.height / viewBoxH);
        const offsetX = (rect.width - viewBoxW * scale) / 2;
        const cursorX = (event.clientX - rect.left - offsetX) / scale;
        // Find nearest sample by X distance.
        let nearest = 0;
        let best = Infinity;
        for (let i = 0; i < chart.pts.length; i += 1) {
            const d = Math.abs(chart.pts[i].x - cursorX);
            if (d < best) {
                best = d;
                nearest = i;
            }
        }
        setHoverIdx(nearest);
    }

    return (
        <div className="flex flex-col rounded-[10px] border border-[#bfb67b] bg-[#10281b] p-3">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[13px] font-bold text-[#e6c985]">Total Deployed</h3>
                <div ref={pickerRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setPickerOpen((v) => !v)}
                        aria-haspopup="menu"
                        aria-expanded={pickerOpen}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#bfb67b] px-2.5 py-0.5 text-[10px] font-semibold text-[#e6c985] hover:bg-[#162e1f]"
                    >
                        {currentWindow.label}
                        <ChevronDownSmall />
                    </button>
                    {pickerOpen ? (
                        <ul
                            role="menu"
                            className="absolute right-0 top-full z-20 mt-1 w-28 overflow-hidden rounded-md border border-[#bfb67b] bg-[#10281b] shadow-lg"
                        >
                            {WINDOW_OPTIONS.map((opt) => {
                                const isActive = opt.id === windowId;
                                return (
                                    <li key={opt.id}>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => {
                                                setWindowId(opt.id);
                                                setPickerOpen(false);
                                            }}
                                            className={`flex w-full items-center justify-between px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                                                isActive
                                                    ? "bg-[#FBC206] text-[#10281b]"
                                                    : "text-[#e6c985] hover:bg-[#162e1f]"
                                            }`}
                                        >
                                            {opt.label}
                                            {isActive ? <span aria-hidden="true">✓</span> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </div>
            </div>

            {/* Projected Total Returns — combined maximum (all positions'
                full-term payout minus leverage). Distinct from the chart,
                which tracks deployed capital over time. */}
            <div className="mt-2 flex items-baseline justify-between rounded-[8px] bg-[#162e1f] px-3 py-1.5">
                <span className="font-manrope text-[10px] font-semibold uppercase tracking-widest text-[#e6c985]/80">
                    Total Returns · at maturity
                </span>
                <span className="font-manrope text-[14px] font-bold text-white">
                    ${formatUsdcCompact(totalReturns)}
                </span>
            </div>

            <div className="mt-2 overflow-visible">
                {!chart ? (
                    <div className="flex h-44 items-center justify-center text-[11px] text-[#e6c985]/70">
                        {isLoading ? "Loading portfolio history…" : "No deposits yet."}
                    </div>
                ) : (
                    <svg
                        ref={svgRef}
                        viewBox="0 0 640 260"
                        className="h-44 w-full overflow-visible cursor-crosshair"
                        preserveAspectRatio="xMidYMid meet"
                        onPointerMove={handlePointerMove}
                        onPointerLeave={() => setHoverIdx(null)}
                    >
                        <defs>
                            <linearGradient id="returnsArea" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="#FBC206" stopOpacity="0.4" />
                                <stop offset="100%" stopColor="#10281b" stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        {chart.yLabels.map(({ label, y }) => (
                            <g key={label}>
                                <text x="2" y={y + 4} fontSize="11" fill="#e6c985" opacity="0.7">{label}</text>
                                <line x1="40" x2="640" y1={y} y2={y} stroke="#ffffff" strokeOpacity="0.1" strokeDasharray="3,5" />
                            </g>
                        ))}

                        <path d={chart.areaD} fill="url(#returnsArea)" />
                        <path d={chart.lineD} fill="none" stroke="#FBC206" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />

                        {/* Transparent overlay that captures hover anywhere
                            inside the chart area, not just on the line. */}
                        <rect x="40" y="20" width="600" height="200" fill="transparent" />

                        {activePt ? (
                            <>
                                <line
                                    x1={activePt.x}
                                    x2={activePt.x}
                                    y1="20"
                                    y2="220"
                                    stroke="#e6c985"
                                    strokeOpacity="0.4"
                                    strokeDasharray="3,5"
                                />
                                <g transform={`translate(${activePt.x}, ${activePt.y})`}>
                                    <circle r="5" fill="#FBC206" stroke="#10281b" strokeWidth="2" />
                                    <g transform="translate(0, -22)">
                                        <rect x={rectX} y="-12" width={tooltipWidth} height="22" rx="11" fill="#FBC206" />
                                        <text x={textX} y="3" fontSize="11" fill="#10281b" textAnchor={anchor} fontWeight="700">
                                            {tooltipText}
                                        </text>
                                    </g>
                                </g>
                            </>
                        ) : null}

                        {chart.xLabels.map(({ label, x }) => {
                            const xa = x > 540 ? "end" : x < 100 ? "start" : "middle";
                            return (
                                <text key={label} x={x} y="248" fontSize="11" fill="#e6c985" opacity="0.7" textAnchor={xa}>
                                    {label}
                                </text>
                            );
                        })}
                    </svg>
                )}
            </div>
        </div>
    );
}

/**
 * Maps the backend's samples payload to renderable chart geometry. Pure
 * formatting — no business logic. Returns null when the wallet has no
 * history yet so the card can render an empty-state instead of a chart.
 */
function projectChartFromBackend(data) {
    if (!data || !data.samples || data.samples.length < 2) return null;
    const left = 40;
    const right = 630;
    const top = 30;
    const bottom = 220;

    const values = data.samples.map((s) => Number(s.value) / 1e6);
    const max = Math.max(...values, 1e-6);
    const min = 0;
    const span = max - min || 1;
    const n = values.length;
    const pts = data.samples.map((s, i) => {
        const x = left + ((right - left) / (n - 1)) * i;
        const v = Number(s.value) / 1e6;
        const y = bottom - ((v - min) / span) * (bottom - top);
        return { x, y, v, timestamp: s.timestamp };
    });
    const lineD = `M ${pts.map((p) => `${p.x},${p.y}`).join(" L ")}`;
    const areaD = `${lineD} L ${right},${bottom} L ${left},${bottom} Z`;
    const yLabels = [
        { label: `$${formatChartAxis(max)}`, y: top },
        { label: `$${formatChartAxis((max + min) / 2)}`, y: top + (bottom - top) / 2 },
        { label: `$${formatChartAxis(min)}`, y: bottom }
    ];
    const windowSec = (data.toTime || 0) - (data.fromTime || 0);
    const windowLabel = describeWindow(windowSec);
    const xLabels = [
        { label: windowLabel === "Live" ? `${Math.round(windowSec / 60)}m ago` : `${windowLabel} ago`, x: left },
        { label: "today", x: right }
    ];
    let peakIdx = 0;
    for (let i = 1; i < pts.length; i += 1) {
        if (pts[i].v > pts[peakIdx].v) peakIdx = i;
    }
    return { pts, lineD, areaD, yLabels, xLabels, peakIdx };
}

/** Tooltip text: shows "Peak $X.XX" before any hover, plain "$X.XX" while hovering. */
function formatTooltip(valueUsdc, isPeakDefault) {
    const amount = `$${formatYieldAmount(BigInt(Math.round((valueUsdc || 0) * 1e6)))}`;
    return isPeakDefault && valueUsdc > 0 ? `Peak ${amount}` : amount;
}

/* -------------------------------------------------------------------------- */
/*  Action Required                                                           */
/* -------------------------------------------------------------------------- */

function ActionRequiredCard({ address, deposits = [], matured = [] }) {
    // Load the user's loans so high-LTV positions surface as actions too.
    const { loans } = useLoansByOwner(address);
    // Use chain time so "matures in X days" badges match the local
    // node's fast-forwarded state.
    const { chainNowSec } = useChainTime();

    const items = useMemo(() => {
        const nowSec = chainNowSec;
        const ONE_DAY = 86_400;
        const out = [];

        // Matured but not yet settled → highest-priority action.
        for (const d of matured) {
            const idPadded = String(d.id).padStart(6, "0");
            out.push({
                id: `matured-${d.id}`,
                to: `/term-deposits/${d.id}`,
                label: `Term Deposit #${idPadded} matured. Settle to claim principal + interest.`,
                severity: "critical"
            });
        }

        // Approaching maturity within 30 days.
        for (const d of deposits) {
            if (Number(d.state) !== 1) continue; // active only
            const remaining = Number(d.maturityTime) - nowSec;
            if (remaining > 0 && remaining < 30 * ONE_DAY) {
                const days = Math.max(1, Math.ceil(remaining / ONE_DAY));
                const idPadded = String(d.id).padStart(6, "0");
                out.push({
                    id: `near-${d.id}`,
                    to: `/term-deposits/${d.id}`,
                    label: `Term Deposit #${idPadded} matures in ${days} day${days === 1 ? "" : "s"}.`,
                    severity: "warning"
                });
            }
        }

        // Loans approaching the liquidation threshold. The warning band starts
        // at the MIDPOINT between the max-borrow LTV and the liquidation
        // threshold (≈80% for 75%/85%), NOT at the max-borrow LTV itself — a
        // freshly drawn loan sits at exactly maxLtv (e.g. Turbo loop rungs at
        // 75%) and is healthy/by-design, not "approaching liquidation". The
        // warning only fires once the LTV has crept up halfway toward liq.
        const liqThreshold = config.protocol.liquidationThresholdBps;
        const warnThreshold = Math.floor((config.protocol.maxLtvBps + liqThreshold) / 2);
        for (const loan of loans) {
            if (!loan.isOpen) continue;
            const ltv = loan.ltvBps;
            if (ltv >= liqThreshold) {
                out.push({
                    id: `liq-${loan.depositId}`,
                    to: `/loans/${loan.depositId}`,
                    label: `Loan #${String(loan.depositId).padStart(6, "0")} is at risk of liquidation (LTV ${(ltv / 100).toFixed(1)}%).`,
                    severity: "critical"
                });
            } else if (ltv >= warnThreshold) {
                out.push({
                    id: `nearliq-${loan.depositId}`,
                    to: `/loans/${loan.depositId}`,
                    label: `Loan #${String(loan.depositId).padStart(6, "0")} approaching liquidation (LTV ${(ltv / 100).toFixed(1)}%).`,
                    severity: "warning"
                });
            }
        }

        // Auto-compound eligibility hint — surfaces when accrued interest
        // is meaningful but compound mode is still Off.
        const trigger = BigInt(config.protocol.autoCompoundTriggerUsdc) * 10n ** BigInt(config.protocol.usdcDecimals);
        for (const d of deposits) {
            if (Number(d.state) !== 1) continue;
            if (Number(d.compoundMode) !== 0) continue;
            if (BigInt(d.accruedInterest || 0) >= trigger) {
                out.push({
                    id: `compound-${d.id}`,
                    to: `/term-deposits`,
                    label: `Enable Automated Reinvestment on Term Deposit #${String(d.id).padStart(6, "0")} to reinvest accrued yield.`,
                    severity: "info"
                });
            }
        }

        // Cap to 5 so the card stays compact.
        return out.slice(0, 5);
    }, [deposits, matured, loans, chainNowSec]);
    return (
        <div className="flex flex-col rounded-[10px] border border-[#bfb67b] bg-[#10281b] p-3">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[13px] font-bold text-[#e6c985]">Action Required</h3>
            </div>
            <ul className="mt-3 divide-y divide-white/10">
                {items.length === 0 ? (
                    <li className="py-3 text-[11px] text-[#e6c985]/70">
                        Nothing to action right now — your positions are all healthy.
                    </li>
                ) : null}
                {items.map((item) => (
                    <li key={item.id} className="flex items-center gap-2.5 py-3 first:pt-1 last:pb-1">
                        <span
                            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                                item.severity === "critical"
                                    ? "bg-[#FF4444]"
                                    : item.severity === "warning"
                                    ? "bg-[#FBC206] text-[#10281b]"
                                    : "bg-[#1D3E29] ring-1 ring-[#e6c985]/40"
                            }`}
                            aria-hidden="true"
                        >
                            !
                        </span>
                        <div className="flex-1 truncate text-[11px] font-medium text-white">{item.label}</div>
                        <Link
                            to={item.to || "/dashboard"}
                            className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-[#e6c985] hover:underline"
                        >
                            View More
                            <svg className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="7 4 13 10 7 16" />
                            </svg>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Allocation donut                                                          */
/* -------------------------------------------------------------------------- */

function AllocationCard({ deposits = [] }) {
    // Allocation by compound mode + lifecycle phase. Each slice's value
    // is the share of total principal (USDC). All three buckets always
    // appear in the legend so the user can compare distribution at a
    // glance — even 0% slices are listed (just absent from the donut).
    //
    // Previously we filtered out 0% slices and the card collapsed to a
    // single "100% Standard" row when a wallet had no Turbo/Matured
    // positions yet, which read as "the page is broken".
    const slices = useMemo(() => {
        let standard = 0n;
        let turbo = 0n;
        let matured = 0n;
        for (const d of deposits) {
            const p = BigInt(d.principal || 0);
            const state = Number(d.state);
            if (state >= 2 && state <= 4) {
                matured += p;
            } else if (state === 1) {
                if (Number(d.compoundMode) === 2) turbo += p;
                else standard += p;
            }
        }
        const total = standard + turbo + matured;
        // One decimal place; BigInt math so we don't lose precision on
        // large principals.
        const toPct = (v) => (total === 0n ? 0 : Number((v * 1000n) / total) / 10);
        return [
            { label: "Standard Bond", value: toPct(standard), color: "#bfb67b", raw: standard },
            { label: "Turbo Bond", value: toPct(turbo), color: "#FBC206", raw: turbo },
            { label: "Matured Bond", value: toPct(matured), color: "#487c3f", raw: matured }
        ];
    }, [deposits]);
    const totalPct = slices.reduce((acc, s) => acc + s.value, 0);
    const radius = 60;
    const stroke = 26;
    const circumSpace = 4;
    const circumference = 2 * Math.PI * radius;
    let offset = 35;
    return (
        <div className="flex flex-col rounded-[10px] border border-[#bfb67b]/40 bg-[#10281b] p-3 text-[#e6c985]">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[13px] font-bold">Allocation</h3>
            </div>
            <div className="mt-7 flex justify-center">
                <svg viewBox="0 0 160 160" className="h-36 w-36">
                    <circle cx="80" cy="80" r={radius} fill="none" stroke="#162e1f" strokeWidth={stroke} />
                    {/* Skip 0% slices in the donut — they'd add a
                        negative-length dash which renders weirdly — but
                        keep them in the legend below for visibility. */}
                    {slices.filter((s) => s.value > 0).map((slice) => {
                        const len = (slice.value / 100) * circumference;
                        const seg = (
                            <circle
                                key={slice.label}
                                cx="80"
                                cy="80"
                                r={radius}
                                fill="none"
                                stroke={slice.color}
                                strokeWidth={stroke}
                                strokeDasharray={`${len - circumSpace} ${circumference - len + circumSpace}`}
                                strokeDashoffset={-offset}
                                transform="rotate(70 80 80)"
                            />
                        );
                        offset += len;
                        return seg;
                    })}
                    {totalPct === 0 ? (
                        <text
                            x="80"
                            y="86"
                            textAnchor="middle"
                            fontSize="11"
                            fill="#e6c985"
                            opacity="0.7"
                        >
                            No positions
                        </text>
                    ) : null}
                </svg>
            </div>
            <ul className="mt-5 space-y-1.5 text-sm">
                {slices.map((s) => (
                    <li key={s.label} className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                            <span
                                className="inline-block h-2 w-2 rounded-full"
                                style={{ backgroundColor: s.color }}
                            />
                            <span className="text-[11px]">{s.label}</span>
                        </span>
                        <span className="text-[11px] font-semibold">{s.value}%</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function TokensCard() {
    // Real GIL balance for the connected wallet — read directly from the
    // GIL ERC-20 (USER's balance, not "GIL bought via your deposits",
    // which actually accrues to Treasury). Re-reads every 15s so a
    // freshly bought / received GIL shows without a page reload. Falls
    // back to "0" silently if the GIL address isn't configured yet.
    const { address } = useWallet();
    const [tokensHeld, setTokensHeld] = useState(0n); // raw GIL wei (18-dp)
    useEffect(() => {
        if (!address || !config.contracts.gil) {
            setTokensHeld(0n);
            return undefined;
        }
        let cancelled = false;
        const provider = getReadProvider();
        const gil = new ethers.Contract(
            config.contracts.gil,
            ["function balanceOf(address) view returns (uint256)"],
            provider
        );
        async function load() {
            try {
                const bal = await gil.balanceOf(address);
                if (!cancelled) setTokensHeld(BigInt(bal));
            } catch {
                /* silent — config / RPC issue, keep last value */
            }
        }
        load();
        const id = setInterval(load, 15_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [address]);
    return (
        <div className="flex flex-col rounded-[10px] border border-[#bfb67b] bg-[#10281b] p-5">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Tokens</h3>
            </div>
            <div className="mt-3 flex items-baseline justify-center gap-1.5">
                <span className="font-manrope text-[20px] font-bold text-white">
                    {formatGilCompact(tokensHeld)}
                </span>
                <span className="text-[14px] font-semibold text-[#e6c985]/80">$GILD</span>
            </div>
            <div className="mt-1 flex items-center justify-center gap-1 text-[10px] text-[#e6c985]/70">
                Total Holdings
                <InfoIcon light />
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Recent Transactions                                                       */
/* -------------------------------------------------------------------------- */

function RecentTransactionsCard({ address }) {
    // Pulled from the backend's projected Transaction feed (indexer
    // builds these from Event rows). Type → "Earned/Invested/..." label
    // mapping lives here, not in the backend, so we can iterate on
    // wording without a redeploy.
    const { items: rawItems, isLoading } = useRecentTransactions(address, { limit: 7 });
    const items = useMemo(() => rawItems.map(formatTransactionRow), [rawItems]);
    return (
        <div className="flex h-full flex-col rounded-[10px] border border-[#bfb67b] bg-[#10281b] p-3">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[13px] font-bold text-[#e6c985]">Recent Transactions</h3>
            </div>
            <ul className="mt-3 space-y-3">
                {isLoading && items.length === 0 ? (
                    <li className="rounded-xl bg-[#162e1f] px-4 py-3 text-[11px] text-[#e6c985]/70 ring-1 ring-[#bfb67b]/40">
                        Loading transactions…
                    </li>
                ) : null}
                {!isLoading && items.length === 0 ? (
                    <li className="rounded-xl bg-[#162e1f] px-4 py-3 text-[11px] text-[#e6c985]/70 ring-1 ring-[#bfb67b]/40">
                        No transactions yet. Deploy capital or open a loan to see activity here.
                    </li>
                ) : null}
                {items.map((item) => (
                    <li
                        key={item.id}
                        className="flex items-center justify-between rounded-xl bg-[#162e1f] px-4 py-1.5 ring-1 ring-[#bfb67b]/40"
                    >
                        <div className="flex items-center gap-3">
                            <span
                                className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-[#e6c985] ring-1 ring-[#e6c985]/30"
                            >
                                {item.positive ? <TrendUpIcon /> : <TrendDownIcon />}
                            </span>
                            <div>
                                <div className="text-[12px] font-light text-white">{item.type}</div>
                                <div className="text-[10px] text-[#e6c985]/70">⏱ {item.time}</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className={`text-[13px] font-semibold ${item.positive ? "text-[#22C55E]" : "text-white"}`}>
                                {item.amount}
                            </div>
                            <div className="text-[11px] text-[#e6c985]/70">{item.credits || ""}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Maps a raw backend Transaction row to the shape the Recent Transactions
 * card renders. `direction` decides red/green colouring; `type` decides
 * the human label. `credits` is a placeholder for the future GIL rewards
 * line — left as the deposit ID for now so the secondary line stays
 * useful instead of empty.
 */
function formatTransactionRow(raw) {
    const TYPE_LABELS = {
        deposit_opened: "Invested",
        deposit_settled: "Settled",
        interest_withdrawn: "Interest Withdrawn",
        deposit_rolled_over: "Rolled Over",
        early_exit: "Early Exit",
        abandoned_fee: "Abandoned Fee",
        loan_opened: "Borrowed",
        loan_repaid: "Repaid",
        loan_liquidated: "Liquidated",
        nft_transfer_in: "NFT In",
        nft_transfer_out: "NFT Out",
        compound_executed: "Automated Reinvestment",
        turbo_executed: "Turbo Loop",
        reinvestment_settlement: "Reinvestment Bonus"
    };
    const positive = raw.direction === "in";
    const amountBase = BigInt(raw.amount || "0");
    const amountUsdc = Number(amountBase) / 1e6;
    const sign = positive ? "+" : "-";
    return {
        id: `${raw.txHash}-${raw.logIndex}`,
        type: TYPE_LABELS[raw.type] || raw.type,
        amount: amountBase === 0n
            ? "—"
            : `${sign}$${amountUsdc.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
        credits: raw.depositId != null ? `TD #${String(raw.depositId).padStart(6, "0")}` : "",
        time: relativeTime(raw.timestamp),
        positive
    };
}

function relativeTime(iso) {
    if (!iso) return "";
    // "X min ago" of an on-chain event — measure elapsed against chain time
    // so it stays correct under local time-warp (chain runs ahead of wall).
    const ms = chainNowMs() - new Date(iso).getTime();
    const min = Math.floor(ms / 60_000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
    const mo = Math.floor(day / 30);
    return `${mo} mo${mo === 1 ? "" : "s"} ago`;
}

/* -------------------------------------------------------------------------- */
/*  Protocol Health                                                           */
/* -------------------------------------------------------------------------- */

function ProtocolHealthCard() {
    const { data: treasury, isLoading: treasuryLoading } = useTreasury();
    const { data: lendingHealth } = useLendingHealth();
    const { status: peg } = usePegStatus();
    const [coverage, setCoverage] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchInterestCoverage();
                if (!cancelled) setCoverage(data);
            } catch {
                /* leave null */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const tvl = treasury?.totalAllocated || 0n;
    const interestReserve = treasury?.interestReserve || 0n;
    const buybackReserve = treasury?.buybackReserve || 0n;
    const operationalReserve = treasury?.operationalReserve || 0n;
    // Lending Utilisation = loans drawn ÷ borrowable capacity. The capacity is
    // the SafeVault segregated principal × maxLTV (NOT treasury.totalAllocated,
    // the Distribution Reserve — that gave >100% garbage like 574%).
    const borrowBase = lendingHealth?.safeVaultPrincipal
        ? (BigInt(lendingHealth.safeVaultPrincipal) * BigInt(config.protocol.maxLtvBps)) / 10000n
        : 0n;
    // null (→ "—") when the borrow base isn't available yet, so we never show a
    // misleading "0.0%" while the SafeVault figure is still loading.
    const utilisation = lendingHealth && borrowBase > 0n
        ? Number((BigInt(lendingHealth.totalOutstandingPrincipal || "0") * 10000n) / borrowBase) / 100
        : null;
    const coverageRatioBps = coverage?.coverageRatioBps ?? null;
    const coverageOk = coverageRatioBps == null || coverageRatioBps >= 10_000;
    const tier1Tone = coverageRatioBps == null
        ? "neutral"
        : coverageRatioBps >= 10_000
        ? "ok"
        : coverageRatioBps >= 7_500
        ? "warn"
        : "danger";

    return (
        <div className="rounded-[10px] border border-[#bfb67b] bg-[#10281b] px-3 pt-5 pb-3">
            <div className="flex items-center justify-between gap-2">
                <h3 className="font-manrope text-[13px] font-bold text-[#e6c985]">Protocol Health</h3>
                <ZoneIndicator compact />
            </div>
            <div className="mt-7 grid grid-cols-2 gap-3">
                <div>
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#e6c985]/70">
                        Distribution Reserve TVL
                    </div>
                    <div className="mt-2.5 font-manrope text-[16px] font-bold text-white">
                        {treasuryLoading ? (
                            <Shimmer width="5rem" height="1rem" tone="light" />
                        ) : (
                            `$${formatUsdcCompact(tvl)}`
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#e6c985]/70">
                        Lending Utilisation
                    </div>
                    <div className="mt-2.5 font-manrope text-[16px] font-bold text-white">
                        {treasuryLoading ? (
                            <Shimmer width="3rem" height="1rem" tone="light" />
                        ) : utilisation != null ? (
                            `${utilisation.toFixed(1)}%`
                        ) : (
                            "—"
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-6 h-1.5 overflow-hidden rounded-full bg-white/10">
                <div
                    className="h-full rounded-full bg-[#FBC206]"
                    style={{ width: `${Math.min(100, utilisation || 0)}%` }}
                />
            </div>
            <div className="mt-4 flex items-center justify-between text-[10px] text-[#e6c985]/80">
                <span>USDC Peg</span>
                <span className={`font-semibold ${peg?.isPegged === false ? "text-[#FF4444]" : "text-[#22C55E]"}`}>
                    {peg?.isPegged === false ? "De-pegged" : "Healthy"}
                </span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
                <KpiPill
                    label="Tier 1 · Interest"
                    value={treasuryLoading ? null : `$${formatUsdcCompact(interestReserve)}`}
                    loading={treasuryLoading}
                    tone={tier1Tone}
                />
                <KpiPill
                    label="Tier 2 · Buyback"
                    value={treasuryLoading ? null : `$${formatUsdcCompact(buybackReserve)}`}
                    loading={treasuryLoading}
                />
                <KpiPill
                    label="Operational"
                    value={treasuryLoading ? null : `$${formatUsdcCompact(operationalReserve)}`}
                    loading={treasuryLoading}
                />
            </div>
            <div className="mt-3 flex items-center justify-between text-[10px] text-[#e6c985]/80">
                <span>Tier 1 Coverage</span>
                <span className={`font-semibold ${
                    tier1Tone === "ok"
                        ? "text-[#22C55E]"
                        : tier1Tone === "warn"
                        ? "text-[#FBC206]"
                        : tier1Tone === "danger"
                        ? "text-[#FF4444]"
                        : "text-[#e6c985]/70"
                }`}>
                    {coverageRatioBps == null
                        ? "—"
                        : `${(coverageRatioBps / 100).toFixed(1)}%`}
                </span>
            </div>
        </div>
    );
}

function KpiPill({ label, value, loading = false, tone = "neutral" }) {
    const ringTone =
        tone === "ok"
            ? "ring-[#22C55E]/60"
            : tone === "warn"
            ? "ring-[#FBC206]/60"
            : tone === "danger"
            ? "ring-[#FF4444]/60"
            : "ring-[#bfb67b]/40";
    return (
        <div className={`rounded-xl bg-[#162e1f] ring-1 px-3 py-2 ${ringTone}`}>
            <div className="text-[9px] font-bold uppercase tracking-widest text-[#e6c985]/70">{label}</div>
            <div className="mt-0.5 font-manrope text-[16px] font-bold text-white">
                {loading ? <Shimmer width="3rem" height="1rem" tone="light" /> : value}
            </div>
        </div>
    );
}

function TvgBackingCard() {
    const { current } = useTvt();
    const [windowId, setWindowId] = useState("30d");
    const { data, isLoading } = useTvtSeries({ windowId, samples: 61 });
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);
    useEffect(() => {
        if (!pickerOpen) return undefined;
        const close = (event) => {
            if (!pickerRef.current?.contains(event.target)) setPickerOpen(false);
        };
        const esc = (event) => {
            if (event.key === "Escape") setPickerOpen(false);
        };
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", esc);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", esc);
        };
    }, [pickerOpen]);

    // Merge the LIVE TVT reading into the historical series as a
    // synthetic "now" sample BEFORE projecting the chart geometry.
    // Without this, the chart's right edge stays pegged to the latest
    // periodic snapshot (TVT_SNAPSHOT_INTERVAL_MS = 5 min by default),
    // so a freshly bumped TVT (e.g. a deposit just bought GIL and
    // pushed price up) read live in the header at $0.0403 was visibly
    // disagreeing with the chart's last point at $0.0215. Live wins
    // because it's read fresh on every poll.
    const chart = useMemo(() => {
        if (!data) return null;
        // Live value only counts if the backend marked it valid AND
        // it's STRICTLY newer than the latest historical sample. We
        // don't drop in a synthetic point if the chain reverted or
        // the history is already up to date.
        const hasLive = current && current.tvtValid && current.tvt !== null;
        if (!hasLive) return projectTvtChartFromBackend(data);
        // Chain seconds so the synthetic live sample sorts/aligns with the
        // chain-time observedAt timestamps on the historical TVT samples.
        const liveSampleTime = chainNowSec();
        const lastSampleTime = data.samples?.[data.samples.length - 1]?.timestamp ?? 0;
        if (liveSampleTime <= lastSampleTime) {
            return projectTvtChartFromBackend(data);
        }
        const synthetic = {
            timestamp: liveSampleTime,
            tvt: current.tvt.toString(),
            circulatingSupply: current.circulatingSupply?.toString() || "0",
            totalAllocated: current.totalAllocated?.toString() || "0"
        };
        return projectTvtChartFromBackend({
            ...data,
            samples: [...(data.samples || []), synthetic],
            toTime: liveSampleTime
        });
    }, [data, current]);
    const currentWindow = WINDOW_OPTIONS.find((w) => w.id === windowId) || WINDOW_OPTIONS[4];
    // TVT raw = treasuryUSDC(6dp) × 1e18 / circulatingGIL(18dp) = humanTVT × 1e6,
    // so divide by 1e6 to get USDC-per-GIL. (Was wrongly /1e18.)
    //
    // `current?.tvt` is now null (not 0n) when the on-chain getter
    // reverted — so the explicit null check distinguishes "TVT is
    // genuinely $0.0000" (show the value) from "TVT can't be computed
    // yet" (show "—"). Previously `tvt ? show : "—"` lumped both into
    // the dash and the user saw the header flicker between $0.0215
    // and "—" as transient reverts came and went.
    // Derive TVG from treasury ÷ circulating supply (full precision) instead of
    // the on-chain treasuryValuePerToken, which rounds to 0 early and showed
    // "$0.0000". Matches the My Tokens page.
    const derivedTvg = current
        ? deriveTvgUsd(current.totalAllocated, current.circulatingSupply)
        : null;
    const tvtDisplay = derivedTvg != null ? formatTvtUsd(derivedTvg) : null;
    // Live GIL market price (spotPrice is USDC-per-GIL × 1e8 → /1e8). Shown
    // next to the TVG floor so users see market vs guaranteed backing.
    const priceDisplay = current && current.spotPrice !== null && current.spotPrice !== undefined
        ? formatTvtUsd(Number(current.spotPrice) / 1e8)
        : null;
    // GIL is an 18-decimal token — must use the GIL formatter, never the
    // 6-decimal USDC one. `circulatingSupply` excludes Treasury-held
    // inventory (spec §9.2), so it reads ~100K of the 1M total, not 1M.
    const circulatingDisplay = current?.circulatingSupply
        ? formatGilCompact(current.circulatingSupply)
        : null;
    const totalSupplyDisplay = current?.totalSupply ? formatGilCompact(current.totalSupply) : null;

    // Same hover model as TotalReturnsCard — cursor follows, peak shown
    // by default. See projectTvtChartFromBackend for the per-point shape.
    const [hoverIdx, setHoverIdx] = useState(null);
    const svgRef = useRef(null);
    const activeIdx = hoverIdx ?? chart?.peakIdx ?? (chart ? chart.pts.length - 1 : 0);
    const activePt = chart?.pts?.[activeIdx];
    const tooltipText = activePt ? formatTvtTooltip(activePt.v, hoverIdx == null) : "";
    const tooltipWidth = Math.max(96, tooltipText.length * 7.5 + 16);
    const anchor = activePt && activePt.x > 540
        ? "end"
        : activePt && activePt.x < 100
        ? "start"
        : "middle";
    const rectX = anchor === "end" ? -tooltipWidth + 12 : anchor === "start" ? -12 : -tooltipWidth / 2;
    const textX = anchor === "end" ? -6 : anchor === "start" ? 6 : 0;

    function handlePointerMove(event) {
        if (!chart || !svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const viewBoxW = 640;
        const viewBoxH = 260;
        const scale = Math.min(rect.width / viewBoxW, rect.height / viewBoxH);
        const offsetX = (rect.width - viewBoxW * scale) / 2;
        const cursorX = (event.clientX - rect.left - offsetX) / scale;
        let nearest = 0;
        let best = Infinity;
        for (let i = 0; i < chart.pts.length; i += 1) {
            const d = Math.abs(chart.pts[i].x - cursorX);
            if (d < best) {
                best = d;
                nearest = i;
            }
        }
        setHoverIdx(nearest);
    }

    return (
        <div className="flex flex-col rounded-[10px] border border-[#bfb67b] bg-[#10281b] p-3">
            <div className="flex items-center justify-between">
                <h3 className="font-manrope text-[13px] font-bold text-[#e6c985]">TVG Backing</h3>
                <div ref={pickerRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setPickerOpen((v) => !v)}
                        aria-haspopup="menu"
                        aria-expanded={pickerOpen}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#bfb67b] px-2.5 py-0.5 text-[10px] font-semibold text-[#e6c985] hover:bg-[#162e1f]"
                    >
                        {currentWindow.label}
                        <ChevronDownSmall />
                    </button>
                    {pickerOpen ? (
                        <ul
                            role="menu"
                            className="absolute right-0 top-full z-20 mt-1 w-28 overflow-hidden rounded-md border border-[#bfb67b] bg-[#10281b] shadow-lg"
                        >
                            {WINDOW_OPTIONS.map((opt) => {
                                const isActive = opt.id === windowId;
                                return (
                                    <li key={opt.id}>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => {
                                                setWindowId(opt.id);
                                                setPickerOpen(false);
                                            }}
                                            className={`flex w-full items-center justify-between px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                                                isActive
                                                    ? "bg-[#FBC206] text-[#10281b]"
                                                    : "text-[#e6c985] hover:bg-[#162e1f]"
                                            }`}
                                        >
                                            {opt.label}
                                            {isActive ? <span aria-hidden="true">✓</span> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </div>
            </div>
            <div className="mt-2 text-[11px] text-white/80">
                Current TVG : <span className="font-bold text-white">{tvtDisplay ?? "—"}</span>
                <span className="ml-2">Price : <span className="font-bold text-[#FBC206]">{priceDisplay ?? "—"}</span></span>
                {circulatingDisplay ? (
                    <span className="ml-2 text-[10px] text-[#e6c985]/70">
                        circulating {circulatingDisplay}
                        {totalSupplyDisplay ? ` / ${totalSupplyDisplay} total` : ""} $GILD
                    </span>
                ) : null}
            </div>
            <div className="mt-3 overflow-visible">
                {!chart ? (
                    <div className="flex h-44 items-center justify-center text-[11px] text-[#e6c985]/70">
                        {isLoading ? "Loading TVG history…" : "No TVG snapshots yet."}
                    </div>
                ) : (
                    <svg
                        ref={svgRef}
                        viewBox="0 0 640 260"
                        className="h-44 w-full overflow-visible cursor-crosshair"
                        preserveAspectRatio="xMidYMid meet"
                        onPointerMove={handlePointerMove}
                        onPointerLeave={() => setHoverIdx(null)}
                    >
                        <defs>
                            <linearGradient id="tvgArea" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="#FBC206" stopOpacity="0.4" />
                                <stop offset="100%" stopColor="#10281b" stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        {chart.yLabels.map(({ label, y }) => (
                            <g key={label}>
                                <text x="2" y={y + 4} fontSize="11" fill="#e6c985" opacity="0.7">{label}</text>
                                <line x1="40" x2="640" y1={y} y2={y} stroke="#ffffff" strokeOpacity="0.1" strokeDasharray="3,5" />
                            </g>
                        ))}

                        <path d={chart.areaD} fill="url(#tvgArea)" />
                        <path d={chart.lineD} fill="none" stroke="#FBC206" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />

                        <rect x="40" y="20" width="600" height="200" fill="transparent" />

                        {activePt ? (
                            <>
                                <line
                                    x1={activePt.x}
                                    x2={activePt.x}
                                    y1="20"
                                    y2="220"
                                    stroke="#e6c985"
                                    strokeOpacity="0.4"
                                    strokeDasharray="3,5"
                                />
                                <g transform={`translate(${activePt.x}, ${activePt.y})`}>
                                    <circle r="5" fill="#FBC206" stroke="#10281b" strokeWidth="2" />
                                    <g transform="translate(0, -22)">
                                        <rect x={rectX} y="-12" width={tooltipWidth} height="22" rx="11" fill="#FBC206" />
                                        <text x={textX} y="3" fontSize="11" fill="#10281b" textAnchor={anchor} fontWeight="700">
                                            {tooltipText}
                                        </text>
                                    </g>
                                </g>
                            </>
                        ) : null}

                        {chart.xLabels.map(({ label, x }) => {
                            const xa = x > 540 ? "end" : x < 100 ? "start" : "middle";
                            return (
                                <text key={label} x={x} y="248" fontSize="11" fill="#e6c985" opacity="0.7" textAnchor={xa}>
                                    {label}
                                </text>
                            );
                        })}
                    </svg>
                )}
            </div>
        </div>
    );
}

/**
 * Maps the backend's TVT samples payload to renderable chart geometry.
 * TVT is stored as fixed-point (1e18) per the contract; here we convert
 * to a plain float for axis math.
 */
// Intrinsic value (TVG) per token in plain USDC, derived at full precision
// from treasury USDC (6dp) ÷ circulating GIL (18dp). The on-chain
// `treasuryValuePerToken` rounds to 0 while the treasury is small next to the
// multi-billion circulating supply, so reading it directly showed "$0.0000".
// Matches the My Tokens page + the card's "Treasury USDC / Circulating Supply".
function deriveTvgUsd(alloc, circ) {
    const a = Number(alloc || 0) / 1e6; // treasury USDC
    const c = Number(circ || 0) / 1e18; // circulating GIL
    if (!(c > 0)) return null;
    return a / c; // USDC per GIL
}

// Per-sample TVG for the chart: prefer the derived intrinsic (full precision);
// fall back to the stored humanTVT×1e6 for samples missing the raw fields.
function sampleTvg(s) {
    const d = deriveTvgUsd(s.totalAllocated, s.circulatingSupply);
    return d != null ? d : Number(s.tvt) / 1e6;
}

function projectTvtChartFromBackend(data) {
    if (!data || !data.samples || data.samples.length < 2) return null;
    const left = 40;
    const right = 630;
    const top = 30;
    const bottom = 220;

    // Derived TVG per sample (treasury ÷ supply) — the raw s.tvt rounds to 0
    // in the early stage, which flat-lined the chart at $0.0000.
    const values = data.samples.map((s) => sampleTvg(s));
    const max = Math.max(...values, 1e-6);
    const min = Math.min(...values, 0);
    const span = max - min || 1;
    const n = values.length;
    const pts = data.samples.map((s, i) => {
        const x = left + ((right - left) / (n - 1)) * i;
        // Derived TVG — same scale as `values` above so the plotted line +
        // peak align with the Y-axis (raw s.tvt rounds to 0 in early stage).
        const v = sampleTvg(s);
        const y = bottom - ((v - min) / span) * (bottom - top);
        return { x, y, v, timestamp: s.timestamp };
    });
    const lineD = `M ${pts.map((p) => `${p.x},${p.y}`).join(" L ")}`;
    const areaD = `${lineD} L ${right},${bottom} L ${left},${bottom} Z`;
    const yLabels = [
        { label: formatTvtUsd(max), y: top },
        { label: formatTvtUsd((max + min) / 2), y: top + (bottom - top) / 2 },
        { label: formatTvtUsd(min), y: bottom }
    ];
    const windowSec = (data.toTime || 0) - (data.fromTime || 0);
    const windowLabel = describeWindow(windowSec);
    const xLabels = [
        { label: windowLabel === "Live" ? `${Math.round(windowSec / 60)}m ago` : `${windowLabel} ago`, x: left },
        { label: "now", x: right }
    ];
    let peakIdx = 0;
    for (let i = 1; i < pts.length; i += 1) {
        if (pts[i].v > pts[peakIdx].v) peakIdx = i;
    }
    return { pts, lineD, areaD, yLabels, xLabels, peakIdx };
}

// TVT per-GIL backing is tiny while circulating supply is in the billions
// (e.g. ~$0.0000016/GIL with 44B GIL circulating). Four decimals rounds that
// to "$0.0000" and hides the real — and rising — value. Use significant-figure
// precision for sub-0.0001 values so the number is visible; plain 4dp above.
function formatTvtUsd(human) {
    const n = Number(human) || 0;
    if (n <= 0) return "$0.0000";
    if (n < 0.0001) {
        // Plain decimal with ~3 significant figures — NEVER scientific
        // notation (toPrecision emits "5.00e-7" below 1e-6, which users
        // can't read). e.g. 5e-7 → "$0.000000500", 1.58e-6 → "$0.00000158".
        const decimals = Math.min(20, Math.max(4, 2 - Math.floor(Math.log10(n))));
        return `$${n.toFixed(decimals)}`;
    }
    return `$${n.toFixed(4)}`;
}

function formatTvtTooltip(value, isPeakDefault) {
    const amount = formatTvtUsd(value);
    return isPeakDefault && value > 0 ? `Peak ${amount}` : amount;
}

/* -------------------------------------------------------------------------- */
/*  Chart geometry helper                                                     */
/* -------------------------------------------------------------------------- */

function buildReturnsChart() {
    const left = 40;
    const right = 630;
    const top = 30;
    const bottom = 220;
    const yLabels = [
        { label: "12K", y: top },
        { label: "11.5K", y: top + (bottom - top) * 0.25 },
        { label: "11K", y: top + (bottom - top) * 0.5 },
        { label: "10.5K", y: top + (bottom - top) * 0.75 },
        { label: "10K", y: bottom }
    ];
    const xLabels = ["·", "5 Oct", "10 Oct", "15 Oct", "20 Oct", "25 Oct", "30 Oct"].map((label, i) => ({
        label,
        x: left + ((right - left) / 6) * i
    }));
    const xTicks = xLabels.slice(1).map((t) => t.x);

    const pts = Array.from({ length: 30 }, (_, i) => {
        const x = left + ((right - left) / 29) * i;
        const t = i / 29;
        const eased = 1 - Math.pow(1 - t, 1.4);
        const wobble = Math.sin(i * 0.7) * 4;
        const y = bottom - (bottom - top) * eased * 0.85 + wobble;
        return { x, y };
    });
    const lineD = `M ${pts.map((p) => `${p.x},${p.y}`).join(" L ")}`;
    const areaD = `${lineD} L ${right},${bottom} L ${left},${bottom} Z`;

    const highlightIndex = 22;
    const highlight = pts[highlightIndex];

    return { yLabels, xLabels, xTicks, lineD, areaD, highlight };
}

/**
 * Lookback presets for the Total Returns chart. `id` matches the value
 * stored in TotalReturnsCard state; `seconds` is null for "all" (zooms
 * back to the wallet's earliest deposit).
 */
const WINDOW_OPTIONS = [
    { id: "1h", label: "1H", seconds: 60 * 60 },
    { id: "5h", label: "5H", seconds: 5 * 60 * 60 },
    { id: "7d", label: "7D", seconds: 7 * 24 * 60 * 60 },
    { id: "15d", label: "15D", seconds: 15 * 24 * 60 * 60 },
    { id: "30d", label: "30D", seconds: 30 * 24 * 60 * 60 },
    { id: "all", label: "All", seconds: null }
];

// NOTE: portfolio-value-over-time replay now lives on the backend at
// `transaction.controller.js → portfolioHistory`. The frontend renders
// what the API returns via `projectChartFromBackend` above — no more
// client-side reconstruction of the series.

/**
 * Formats USDC base units for the Total Returns chart with enough
 * precision to be useful at every magnitude:
 *   - >= $1            → 2 decimals  "$12.34"
 *   - >= $0.01         → 4 decimals  "$0.0123"
 *   - >= 1 base unit   → 6 decimals  "$0.000038"
 *   - 0                → "$0.00"
 *
 * Without this a freshly-opened deposit (whose minute-scale accrual is
 * micro-USDC) was being formatted as "$0.00" by the standard 2-decimal
 * compact formatter.
 */
function formatYieldAmount(amount) {
    if (!amount) return "0.00";
    const value = Number(amount) / 1e6;
    if (value >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    if (value >= 0.01) return value.toFixed(4);
    if (value > 0) return value.toFixed(6);
    return "0.00";
}

function formatYieldPct(yieldAmt, principal) {
    if (!principal || principal === 0n) return "0";
    const pct = (Number(yieldAmt) / Number(principal)) * 100;
    if (pct >= 1) return pct.toFixed(2);
    if (pct >= 0.01) return pct.toFixed(4);
    if (pct > 0) return pct.toFixed(6);
    return "0";
}

function formatChartAxis(value) {
    if (value >= 100) return value.toFixed(0);
    if (value >= 1) return value.toFixed(2);
    if (value >= 0.01) return value.toFixed(3);
    if (value > 0) return value.toFixed(6);
    return "0";
}

function describeWindow(seconds) {
    const days = Math.floor(seconds / 86400);
    if (days >= 1) return `${days}d`;
    const hours = Math.floor(seconds / 3600);
    if (hours >= 1) return `${hours}h`;
    return "Live";
}

/**
 * Builds the same chart shape as buildReturnsChart but from live TVT
 * history snapshots. Returns null when the dataset is too thin to plot
 * (caller falls back to the placeholder geometry so the card never
 * looks broken during early protocol life).
 */
function buildTvtChart(history) {
    if (!history || history.length < 2) return null;
    const left = 40;
    const right = 630;
    const top = 30;
    const bottom = 220;

    const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp);
    const values = sorted.map((p) => Number(p.tvt) / 1e18);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;

    const n = sorted.length;
    const pts = sorted.map((p, i) => {
        const x = left + ((right - left) / (n - 1)) * i;
        const v = Number(p.tvt) / 1e18;
        const y = bottom - ((v - min) / span) * (bottom - top);
        return { x, y };
    });
    const lineD = `M ${pts.map((p) => `${p.x},${p.y}`).join(" L ")}`;
    const areaD = `${lineD} L ${right},${bottom} L ${left},${bottom} Z`;

    const yLabels = [
        { label: `${max.toFixed(3)}`, y: top },
        { label: `${((max + min) / 2).toFixed(3)}`, y: top + (bottom - top) / 2 },
        { label: `${min.toFixed(3)}`, y: bottom }
    ];
    const xLabels = [
        { label: "earliest", x: left },
        { label: "now", x: right }
    ];
    const xTicks = [];
    const highlight = pts[pts.length - 1];

    return { yLabels, xLabels, xTicks, lineD, areaD, highlight };
}

/* -------------------------------------------------------------------------- */
/*  Tiny icons used by the insights widgets                                   */
/* -------------------------------------------------------------------------- */

function InfoIcon({ light = false }) {
    return (
        <svg className={`h-3 w-3 ${light ? "text-[#e6c985]/70" : "text-[#10281b]/60"}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <circle cx="8" cy="8" r="6" />
            <path d="M8 7v4" />
            <circle cx="8" cy="5" r="0.6" fill="currentColor" />
        </svg>
    );
}

function ChevronDownSmall() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="4 7 10 13 16 7" />
        </svg>
    );
}

function TrendUpIcon() {
    return (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 17 9 11 13 15 21 7" />
            <polyline points="14 7 21 7 21 14" />
        </svg>
    );
}

function TrendDownIcon() {
    return (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 7 9 13 13 9 21 17" />
            <polyline points="14 17 21 17 21 10" />
        </svg>
    );
}
